/* biome-ignore-all lint/suspicious/noExplicitAny: filter form */
import type React from "react"
import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Space } from "antd"
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons"
import dayjs, { type Dayjs } from "dayjs"

const { RangePicker } = DatePicker

export interface ReadingPracticeFilterValues {
  search?: string
  status?: string
  difficulty?: string
  selected_type?: string
  date_range?: [Dayjs, Dayjs] | null
  start_date?: string
  end_date?: string
}

interface ReadingPracticeFilterFormProps {
  onSearch: (values: ReadingPracticeFilterValues) => void
  loading?: boolean
  initialValues?: ReadingPracticeFilterValues
}

export const STATUS_OPTIONS = [
  { label: "Tất cả trạng thái", value: "" },
  { label: "Đã hoàn thành", value: "completed" },
  { label: "Chưa hoàn thành", value: "init" },
]

export const DIFFICULTY_OPTIONS = [
  { label: "Tất cả độ khó", value: "" },
  { label: "Dễ", value: "easy" },
  { label: "Trung bình", value: "medium" },
  { label: "Khó", value: "hard" },
]

export const QUESTION_TYPE_OPTIONS = [
  { label: "Tất cả dạng bài", value: "" },
  { label: "Điền từ", value: "cloze" },
  { label: "Trắc nghiệm", value: "mcq" },
  { label: "Đúng / Sai", value: "tf" },
]

export const ReadingPracticeFilterForm: React.FC<ReadingPracticeFilterFormProps> = ({
  onSearch,
  loading = false,
  initialValues,
}) => {
  const [form] = Form.useForm()

  const handleFinish = (values: any) => {
    let start_date: string | undefined
    let end_date: string | undefined

    if (values.date_range && values.date_range.length === 2) {
      start_date = values.date_range[0]?.format("YYYY-MM-DD")
      end_date = values.date_range[1]?.format("YYYY-MM-DD")
    }

    onSearch({
      search: values.search?.trim() || undefined,
      status: values.status || undefined,
      difficulty: values.difficulty || undefined,
      selected_type: values.selected_type || undefined,
      start_date,
      end_date,
    })
  }

  const handleReset = () => {
    form.resetFields()
    onSearch({
      search: undefined,
      status: undefined,
      difficulty: undefined,
      selected_type: undefined,
      start_date: undefined,
      end_date: undefined,
    })
  }

  const initialRange: [Dayjs, Dayjs] | undefined =
    initialValues?.start_date && initialValues?.end_date
      ? [dayjs(initialValues.start_date), dayjs(initialValues.end_date)]
      : undefined

  return (
    <Card className="mb-2 shadow-xs">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          search: initialValues?.search || "",
          status: initialValues?.status || "",
          difficulty: initialValues?.difficulty || "",
          selected_type: initialValues?.selected_type || "",
          date_range: initialRange,
        }}
      >
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12} md={5}>
            <Form.Item label="Người dùng (Tên, Email hoặc #ID)" name="search">
              <Input placeholder="Nhập tên, email hoặc #ID..." allowClear />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Form.Item label="Trạng thái" name="status">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Form.Item label="Độ khó bài đọc" name="difficulty">
              <Select options={DIFFICULTY_OPTIONS} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Form.Item label="Dạng câu hỏi" name="selected_type">
              <Select options={QUESTION_TYPE_OPTIONS} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Khoảng ngày làm bài" name="date_range">
              <RangePicker format="DD/MM/YYYY" className="w-full" allowClear />
            </Form.Item>
          </Col>

          <Col xs={24} className="flex justify-end">
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Đặt lại
              </Button>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
                Tìm kiếm
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>
  )
}

export default ReadingPracticeFilterForm
